import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useSchemaKey } from '@/composables/schema'
import { useApiStore } from '@/stores/api'

import Payload from '@/lib/Payload'
import { hotReloadStore } from '@/utils/build'

import type { BinderFormSource, BinderSchema, BinderSection } from '@policyfly/schema/types/schemas/binder'
import type { FormSchema } from '@policyfly/schema/types/shared/formSchema'
import type { APIPayloadResponse } from '@policyfly/utils/types'
import type { ComputedRef, Ref } from 'vue'

export interface FormBinderStore {
  file: Ref<BinderSchema | null>
  responses: Ref<APIPayloadResponse[]>
  schema: ComputedRef<FormSchema<BinderFormSource, BinderSection> | null>
  binderPayload: ComputedRef<Payload | null>
  loadSections: () => Promise<BinderSection[] | undefined>
}

export const useFormBinderStore = defineStore('formBinder', (): FormBinderStore => {
  const file: FormBinderStore['file'] = ref<BinderSchema | null>(null)
  const responses: FormBinderStore['responses'] = ref([])

  const schema: FormBinderStore['schema'] = computed(() => file.value)
  const binderPayload: FormBinderStore['binderPayload'] = computed(() => {
    if (!responses.value || !responses.value.length) return null
    return Payload.fromResponses(responses.value)
  })

  const apiStore = useApiStore()
  const { getKey, updateKey } = useSchemaKey({ data: file })
  const loadSections: FormBinderStore['loadSections'] = async () => {
    const { key, shouldLoad } = getKey()
    if (!shouldLoad) return file.value?.sections
    file.value = null
    const result = await apiStore.schema.binder(key)
    if (result.type.oneofKind !== 'legacy') {
      throw new Error('Unsupported schema type')
    }
    const mod = JSON.parse(result.type.legacy.json) as BinderSchema
    file.value = mod
    updateKey(key)
    return mod.sections
  }

  return {
    file,
    responses,
    binderPayload,
    schema,
    loadSections,
  }
})

hotReloadStore(useFormBinderStore)
